import { Injectable } from '@angular/core';
import { QueryEntity, QueryConfig, Order } from '@datorama/akita';
import { RegistrationStepStore, RegistrationStepState } from './registration-step.store';
import { RegistrationStep, RegistrationStepSettings } from './models/registration-step.model';
import { StepType } from '../models/step-type';
import { LoggingService } from '../../shared/services/core/logging.service';
import { WizardType } from '../models/wizard-type';
import { Observable } from 'rxjs';
import { KeyValuePair } from '../../shared/models/key-value-pair.model';

@Injectable({ providedIn: 'root' })
@QueryConfig({ sortBy: 'Order' })
export class RegistrationStepQuery extends QueryEntity<RegistrationStepState> {
  constructor(protected store: RegistrationStepStore, private logService: LoggingService) {
    super(store);
  }

  /** Gets the current registration type that the user is in. */
  public getWizardType(): WizardType | undefined {
    return this.getValue().wizardType;
  }

  public selectWizardType(): Observable<WizardType | undefined> {
    return this.store.selectWizardType();
  }

  /** Gets the current value of the step that matches the given step type. */
  public getStep(stepType: StepType): RegistrationStep | undefined {
    return this.getEntity(stepType);
  }

  /** Gets the current settings of the step */
  public getStepSettings(stepType: StepType): KeyValuePair[] | RegistrationStepSettings | string {
    let settings = this.getStep(stepType)?.Settings || {};

    if( typeof settings === 'object' ) {
        return settings as RegistrationStepSettings;
    }

    return settings;
  }

  /** Gets the first incomplete or errored step for the user to complete next. */
  public getNextStep(): RegistrationStep | null {
    // Get all the steps that aren't completed yet (and shouldn't be skipped) or that have an error.
    const incompleteSteps = this.getAll({ filterBy: (x) => (!x.Complete && !x.ShouldSkip) || !!x.Error });
    return incompleteSteps.length > 0 ? incompleteSteps[0] : null;
  }

  /** Gets the first incomplete or errored step for the user to complete next. */
  public getPreviousStep(stepType?: StepType): RegistrationStep | null {
    const previousSteps = this.getPreviousSteps(stepType);
    if (!previousSteps) {
      return null;
    }

    return previousSteps[previousSteps.length - 1];
  }

  /** Gets all the steps prior to the given step, defaulting to steps prior to the current one if not provided. */
  public getPreviousSteps(stepType?: StepType): RegistrationStep[] {
    // Use the current step if we don't provide a step type
    if (!stepType) {
      const currentStep = this.getPreviousStep();
      if (!currentStep) {
        this.logService.warning('Failed to calculate previous steps as the current step could not be determined.', stepType);
        return [];
      }
      stepType = currentStep.Step;
    }

    const allSteps = this.getAll();

    // Build a list of steps prior to the given one
    let previousSteps: RegistrationStep[] = [];
    for (let step of allSteps) {
      if (step.Step === stepType) {
        break;
      }
      previousSteps.push(step);
    }

    if (allSteps.length === previousSteps.length) {
      // If the entire list of steps was added then that step isn't part of the current process.
      this.logService.warning(
        'Failed to calculate previous steps as the given step type could not be found in the current list of steps.',
        stepType,
        allSteps
      );
    }

    return previousSteps.filter((x) => !x.ShouldSkip);
  }

  /** Gets the final step in the registration process. */
  public getFinalStep(): RegistrationStep | null {
    const lastStepAsArray = this.getAll({ sortByOrder: Order.DESC, limitTo: 1 });
    return lastStepAsArray.length ? lastStepAsArray[0] : null;
  }

  /** Checks if the current step is available to access for the current registration process. */
  public isStepAvailable(stepType: StepType): boolean {
    return this.getCount((x) => x.Step === stepType) > 0;
  }

  /** Determines if the given step, or the current step, has a previous step or not. */
  public hasPreviousStep(stepType?: StepType): boolean {
    return !!this.getPreviousStep(stepType);
  }

  /** Determines if there are any steps that still require completing in the registration process. */
  public anyIncompleteSteps(): boolean {
    return !!this.getCount((x) => !x.Complete || !!x.Error);
  }

  /**
   * Retrieves any data from the store matching the given key that was part of the query string when
   * the current registration was started, or undefined if data with that key was not provided.
   */
  public getQueryData(key: string): string | undefined {
    let result: string | undefined;

    const queryData = this.getValue().queryData;
    if (queryData) {
      const matchingKey = Object.keys(queryData).find((x) => x.toLowerCase() === key.toLowerCase());
      if (matchingKey) {
        result = queryData[matchingKey].toString();
      }
    }

    this.logService.debug(`Retrieving registration query data matching key '${key}':`, result);
    return result;
  }
}
